import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { GALLERY_IMAGES } from "@/data/site";

export const Gallery = () => {
  const [index, setIndex] = useState(null);
  const open = index !== null;
  const total = GALLERY_IMAGES.length;

  const next = useCallback(() => setIndex((i) => (i === null ? 0 : (i + 1) % total)), [total]);
  const prev = useCallback(() => setIndex((i) => (i === null ? 0 : (i - 1 + total) % total)), [total]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  const current = open ? GALLERY_IMAGES[index] : null;

  return (
    <section id="galeria" data-testid="gallery-section" className="section-padding scroll-mt-24 bg-background">
      <div className="container-site flex flex-col gap-14">
        <Reveal>
          <SectionHeading
            id="gallery"
            eyebrow="Galeria"
            title="Um espaço pensado para o seu bem-estar."
            description="Conheça os ambientes da clínica: tons neutros, iluminação suave e conforto em cada detalhe."
          />
        </Reveal>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="gallery-grid">
          {GALLERY_IMAGES.map((img, i) => (
            <Reveal key={img.src} delay={0.06 * i}>
              <button
                type="button"
                onClick={() => setIndex(i)}
                data-testid={`gallery-thumbnail-${i}`}
                aria-label={`Ampliar imagem ${i + 1} de ${total}: ${img.alt}`}
                className="group relative block w-full overflow-hidden rounded-[var(--radius)] border border-border shadow-[0_10px_30px_rgba(20,16,12,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-[hsl(var(--brand-ink))]/0 transition-colors duration-300 group-hover:bg-[hsl(var(--brand-ink))]/15"
                />
                <span
                  aria-hidden="true"
                  className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-card/90 text-foreground opacity-0 shadow-md backdrop-blur transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
                >
                  <Maximize2 className="h-4 w-4" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !v && setIndex(null)}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="z-[var(--z-modal)] max-w-[min(96vw,1100px)] border-none bg-transparent p-0 shadow-none [&>button]:hidden"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">Galeria de fotos da clínica</DialogTitle>
          <DialogDescription className="sr-only">
            Use as setas para navegar entre as imagens e Esc para fechar.
          </DialogDescription>
          {current && (
            <div className="relative overflow-hidden rounded-[var(--radius)] border border-border/40 bg-[hsl(var(--brand-ink))] shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
              <img
                src={current.src}
                alt={current.alt}
                data-testid="gallery-lightbox-image"
                className="max-h-[82vh] w-full object-contain"
              />

              <div className="absolute inset-x-0 top-0 flex items-center justify-between p-3 sm:p-4">
                <span
                  className="rounded-full bg-[hsl(var(--brand-ink))]/70 px-3 py-1.5 text-xs font-medium text-[hsl(var(--brand-warm-white))] backdrop-blur"
                  data-testid="gallery-lightbox-counter"
                >
                  {index + 1} / {total}
                </span>
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  onClick={() => setIndex(null)}
                  aria-label="Fechar galeria"
                  data-testid="gallery-lightbox-close-button"
                  className="h-11 w-11 rounded-full bg-card/90 text-foreground backdrop-blur hover:bg-card"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="absolute inset-y-0 left-0 flex items-center p-2 sm:p-4">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  onClick={prev}
                  aria-label="Imagem anterior"
                  data-testid="gallery-lightbox-prev-button"
                  className="h-11 w-11 rounded-full bg-card/90 text-foreground backdrop-blur hover:bg-card"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center p-2 sm:p-4">
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  onClick={next}
                  aria-label="Próxima imagem"
                  data-testid="gallery-lightbox-next-button"
                  className="h-11 w-11 rounded-full bg-card/90 text-foreground backdrop-blur hover:bg-card"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[hsl(var(--brand-ink))]/70 to-transparent px-5 pb-4 pt-10 text-sm text-[hsl(var(--brand-warm-white))]">
                {current.alt}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
